import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { fetchConversations, setFilters, selectConversation } from '../store/slices/conversationsSlice';
import { fetchMessages, sendMessage, markRead } from '../store/slices/messagesSlice';
import { connectSocket, onSocketEvents, offSocketEvents, joinConversationRoom, leaveConversationRoom, sendTyping } from '../services/socket';
import ConversationList from '../components/messages/ConversationList';
import ConversationHeader from '../components/messages/ConversationHeader';
import ThreadView from '../components/messages/ThreadView';
import Composer from '../components/messages/Composer';
import RightPanel from '../components/messages/RightPanel';
import './admin/AdminMessages.css';

const RetailerInbox = () => {
  const dispatch = useDispatch();
  const { token, user } = useSelector(s => s.auth, shallowEqual);
  const conversations = useSelector(s => s.conversations.list, shallowEqual);
  const convoFilters = useSelector(s => s.conversations.filters, shallowEqual);
  const selectedId = useSelector(s => s.conversations.selectedId);
  const byConv = useSelector(s => s.supportMessages.byConv, shallowEqual);
  const messagesBucket = byConv[selectedId || ''] || { items: [] };
  const [typingMap, setTypingMap] = useState({});
  const typingTimersRef = useRef({}); // { [conversationId]: timeoutId }
  const [isListOpen, setIsListOpen] = useState(false);
  const prevSelectedRef = useRef(null);

  const handleTyping = useMemo(() => ({ conversationId, isTyping, userId }) => {
    if (!conversationId) return;
    if (userId && user && String(userId) === String(user._id)) return;
    setTypingMap(prev => ({ ...prev, [conversationId]: !!isTyping }));
    const timers = typingTimersRef.current || {};
    if (timers[conversationId]) {
      clearTimeout(timers[conversationId]);
      delete timers[conversationId];
    }
    if (isTyping) {
      timers[conversationId] = setTimeout(() => {
        setTypingMap(curr => ({ ...curr, [conversationId]: false }));
        delete timers[conversationId];
      }, 3000);
    }
    typingTimersRef.current = timers;
  }, [user]);

  useEffect(() => {
    if (!token) return;
    connectSocket(token);
    const handlers = { onTyping: handleTyping };
    onSocketEvents(handlers);
    return () => { offSocketEvents(handlers); };
  }, [token, handleTyping]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Default inbox for RTL is support; hide assignee filter usage
    dispatch(fetchConversations({ ...convoFilters, assignee: undefined, inbox: convoFilters.inbox || 'support' }));
  }, [dispatch, convoFilters.page, convoFilters.limit, convoFilters.q, convoFilters.status, convoFilters.tags, convoFilters.inbox]);

  useEffect(() => {
    if (!selectedId && conversations.length > 0) {
      dispatch(selectConversation(conversations[0]._id));
    }
  }, [conversations, selectedId, dispatch]);

  useEffect(() => {
    const prev = prevSelectedRef.current;
    if (prev && prev !== selectedId) leaveConversationRoom(prev);
    if (selectedId) {
      joinConversationRoom(selectedId);
      dispatch(fetchMessages({ conversationId: selectedId, page: 1, limit: 50 }));
      dispatch(markRead({ conversationId: selectedId })); // clears unreadByCustomer
    }
    prevSelectedRef.current = selectedId;
  }, [selectedId, dispatch]);

  const selectedConversation = useMemo(() => conversations.find(c => c._id === selectedId) || null, [conversations, selectedId]);
  const openList = () => setIsListOpen(true);
  const closeList = () => setIsListOpen(false);
  const onSelectConversation = (id) => { dispatch(selectConversation(id)); closeList(); };

  // Auto-open drawer on small screens when no conversation is selected
  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
    if (isMobile && !selectedId) setIsListOpen(true);
  }, [selectedId]);

  const onSend = async ({ channel, text }) => {
    if (!selectedId || !text?.trim()) return;
    await dispatch(sendMessage({ conversationId: selectedId, payload: { channel, bodyText: text } }));
  };

  const onTyping = (isTyping) => { if (selectedId) sendTyping(selectedId, isTyping); };

  return (
    <div className={"admin-messages" + (isListOpen ? ' drawer-open' : '')}>
      <div className="admin-messages__overlay" onClick={closeList} aria-hidden="true" />
      <aside id="inbox-drawer" className="admin-messages__left">
        <div className="admin-messages__left-header">
          <h2>Support</h2>
          <div className="filters">
            <div className="filter filter--search">
              <label className="filter__label" htmlFor="rtl-filter-search">Search</label>
              <input id="rtl-filter-search" placeholder="Search" aria-label="Search" value={convoFilters.q || ''} onChange={(e)=>dispatch(setFilters({ q: e.target.value, page:1 }))} />
            </div>
            <div className="filter filter--status">
              <label className="filter__label" htmlFor="rtl-filter-status">Status</label>
              <select id="rtl-filter-status" aria-label="Status" value={convoFilters.status || ''} onChange={(e)=>dispatch(setFilters({ status: e.target.value || undefined, page:1 }))}>
                <option value="">All</option>
                <option>Open</option>
                <option>Pending</option>
                <option>On Hold</option>
                <option>Closed</option>
              </select>
            </div>
            <div className="filter filter--inbox">
              <label className="filter__label" htmlFor="rtl-filter-inbox">Inbox</label>
              <select id="rtl-filter-inbox" aria-label="Inbox" value={convoFilters.inbox || 'support'} onChange={(e)=>dispatch(setFilters({ inbox: e.target.value || undefined, page:1 }))}>
                <option value="support">Support</option>
                <option value="sales">Sales</option>
                <option value="billing">Billing</option>
              </select>
            </div>
          </div>
        </div>
        <ConversationList items={conversations} selectedId={selectedId} onSelect={onSelectConversation} onClose={closeList} unreadKey="unreadByCustomer" />
      </aside>
      <main className="admin-messages__main">
        <div className="mobile-topbar show-on-mobile" style={{ padding:'8px 12px', borderBottom:'1px solid #eee', justifyContent:'flex-end' }}>
          <button
            onClick={openList}
            aria-label="Open inbox"
            aria-controls="inbox-drawer"
            aria-expanded={isListOpen}
            style={{ padding:'6px 10px', border:'1px solid #ddd', background:'#fff', borderRadius:6 }}
          >
            Inbox
          </button>
        </div>
        {selectedConversation ? (
          <>
            <ConversationHeader conversation={selectedConversation} currentUser={user} readOnlyHeader showBack onBack={openList} />
            <ThreadView messages={messagesBucket.items} currentUser={user} typing={!!typingMap[selectedId]} />
            <Composer onSend={onSend} onTyping={onTyping} canPostNotes={false} />
          </>
        ) : (
          <div className="admin-messages__empty" style={{ textAlign:'center' }}>
            <div style={{ marginBottom:12, color:'#6b7280' }}>Select a conversation to view messages</div>
            <button
              className="show-on-mobile"
              onClick={openList}
              aria-label="Open inbox"
              aria-controls="inbox-drawer"
              aria-expanded={isListOpen}
              style={{ padding:'8px 12px', border:'1px solid #ddd', background:'#fff', borderRadius:8 }}
            >
              Open Inbox
            </button>
          </div>
        )}
      </main>
      {selectedConversation && (
        <aside className="admin-messages__right">
          <RightPanel conversation={selectedConversation} role="RTL" />
        </aside>
      )}
    </div>
  );
};

export default RetailerInbox;
